import { Card, CardContent, CircularProgress, Grid, makeStyles, Typography } from '@material-ui/core';
import React from 'react';
import { Download, Trash } from 'react-feather';
import { TFunction, useTranslation } from 'react-i18next';
import { actions, useTypedDispatch, useTypedSelector } from '../../../../redux';
import { theme } from '../../../../theme/theme';
import { Asset } from '../../../../types/asset';
import { Plan } from '../../../../types/plan';
import { downloadFileDialog, getContentUrl } from '../../../../util/file';
import { IconButton } from '../../../global';
import { SearchBar } from '../../../global/SearchBar';
import { SectionState } from '../InspectionCreationWizard';

const mod = 'sections.References';

/**
 * Number of internal steps this section contains.
 */
export const steps = 1;
export const label = `${mod}.summary-label`;

/**
 * Header generator.
 *
 * @param t translation function
 * @param state the current section state
 * @param focused if is the current section
 * @param asset selected asset
 * @param plan asset plan
 * @returns header title and subtitle
 */
export const header = (t: TFunction<string[]>, state: SectionState, focused: boolean, asset?: Asset, plan?: Plan) => ({
    title: t(`${mod}.header-title${!focused ? '-small' : ''}`),
    subtitle: t(`${mod}.header-subtitle${!focused ? '-small' : ''}`, { count: 1, document: 'API653' }),
});

export type ContentUrl = {
    status: number;
    url: string;
};

const useReferencesClasses = makeStyles({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
        margin: '-5px -10px',
        '& > *': {
            flex: '1 0 calc(100% / 3 - 20px)',
            minWidth: 200,
            margin: '5px 10px',
        },
    },
    searchBar: {
        flex: 1,
        marginBottom: 0,
    },
    option: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: '18px 36px',
        boxShadow: '0 -1px 0 0 rgba(0, 0, 0, 0.12)',
        '& :first-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.content.caption.paragraph,
        },
        '& :last-child': {
            color: theme.colors.darkBlue[5],
            ...theme.typography.content.label.medium,
        },
    },
    cards: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
    },
});

const useReferenceCardClasses = makeStyles({
    card: {
        borderLeft: `5px solid ${theme.colors.darkBlue[5]}`,
    },
    cardContainer: {
        height: '110px',
    },
    referenceHeader: {
        color: theme.colors.neutral[2],
        fontSize: '12px',
        fontFamily: 'Roboto',
    },
    referenceTitle: {
        color: theme.colors.neutral[1],
        fontSize: '18px',
        fontFamily: 'Roboto',
        fontWeight: 500,
    },
    cardButton: {
        justifySelf: 'center',
        alignSelf: 'center',
        color: theme.colors.neutral[2],
    },
    cardText: {
        justifySelf: 'center',
        alignSelf: 'center',
    },
});

const ReferenceCard = (props: { title: string; onDelete: Function; editable: boolean }) => {
    const classes = useReferenceCardClasses();
    const { t } = useTranslation(['inspection-creation']);
    const [isDownloading, setIsDownloading] = React.useState(false);
    const dispatch = useTypedDispatch();

    return (
        <Grid item xs={6}>
            <Card className={classes.card}>
                <CardContent>
                    <Grid container className={classes.cardContainer}>
                        <Grid className={classes.cardText} item xs={10}>
                            <Typography className={classes.referenceTitle}>{props.title}</Typography>
                        </Grid>
                        <Grid className={classes.cardButton} item xs={1}>
                            {/*TODO: Resize spinner to be smaller and change color to Gecko-Green-4*/}
                            {props.editable &&
                                ((isDownloading && <CircularProgress />) || (
                                    <div>
                                        <IconButton
                                            size='small'
                                            title={t(`${mod}.icon-download`)}
                                            onClick={async () => {
                                                setIsDownloading(true);
                                                try {
                                                    const contentUrl = await getContentUrl(props.title);
                                                    if (contentUrl === '') {
                                                        dispatch(
                                                            actions.event.create('error', 'Error retrieving documents'),
                                                        );
                                                    } else {
                                                        await downloadFileDialog(contentUrl);
                                                    }
                                                } catch (err) {
                                                } finally {
                                                    setIsDownloading(false);
                                                }
                                            }}
                                        >
                                            <Download />
                                        </IconButton>
                                    </div>
                                ))}
                        </Grid>
                        <Grid className={classes.cardButton} item xs={1}>
                            {props.editable && (
                                <IconButton
                                    size='small'
                                    title={t(`${mod}.icon-trash`)}
                                    onClick={() => props.onDelete()}
                                >
                                    <Trash />
                                </IconButton>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Grid>
    );
};

export const Component = (props: { state: SectionState; next: () => void }) => {
    const classes = useReferencesClasses();
    const { t } = useTranslation(['inspection-creation']);
    const dispatch = useTypedDispatch();
    const { plan, referenceDocOptions } = useTypedSelector(state => ({
        plan: state.plan.changed,
        referenceDocOptions: state.plan_reference,
    }));
    React.useEffect(() => {
        dispatch(actions.plan_reference.get());
    }, []);

    if (!plan) return <span>{'#plan not in store'}</span>;

    const assetReferenceDocuments = plan.reference_documents ?? [];
    const assetReferenceDocumentIds = assetReferenceDocuments.map(doc => {
        return doc.id;
    });
    const filteredReferenceOptions = referenceDocOptions.items.filter(doc => {
        return !assetReferenceDocumentIds.includes(doc.id);
    });

    if (!props.state.editable && assetReferenceDocuments.length === 0) return <span>{t(`${mod}.empty`)}</span>;

    return (
        <Grid container>
            <Grid item xs={12}>
                {props.state.editable && (
                    <SearchBar
                        className={classes.searchBar}
                        label={t(`${mod}.search-bar-label`)}
                        loading={referenceDocOptions.fetching}
                        options={Object.values(filteredReferenceOptions)}
                        debounce={300}
                        loadingIgnoreOptions
                        renderOption={option => (
                            <div className={classes.option}>
                                <span>{option.file}</span>
                            </div>
                        )}
                        onSelect={doc => {
                            if (!doc) return;
                            const planRefDocs = plan.reference_documents ?? [];
                            const updatedPlanRefDocs = [...planRefDocs, doc];
                            dispatch(actions.plan.edit({ reference_documents: updatedPlanRefDocs }));
                        }}
                        renderLoadingSpinner
                        renderEmpty={t(`${mod}.search-bar-empty`)}
                        renderLabel={doc => `${doc.file}`}
                    />
                )}
            </Grid>
            <Grid container spacing={1}>
                {assetReferenceDocuments.map(referenceDoc => (
                    <ReferenceCard
                        title={referenceDoc.file}
                        onDelete={() => {
                            const filteredReferences = assetReferenceDocuments.filter(doc => {
                                return doc.id !== referenceDoc.id;
                            });
                            dispatch(actions.plan.edit({ reference_documents: filteredReferences }));
                        }}
                        editable={props.state.editable}
                    />
                ))}
            </Grid>
        </Grid>
    );
};
